import { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Film, Trash2, Eye, Clock, CheckCircle, AlertCircle, Play, Upload } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_BADGE = {
  pending: { label: "Pending Review", color: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20" },
  approved: { label: "Published", color: "text-[#00ff77] bg-[#00ff77]/10 border-[#00ff77]/20" },
  rejected: { label: "Rejected", color: "text-red-400 bg-red-400/10 border-red-400/20" },
};

export default function DashboardContent() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadContent(); }, [user]);

  const loadContent = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, "content"), where("creatorId", "==", user.uid), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setContent(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    await deleteDoc(doc(db, "content", id));
    setContent((c) => c.filter((x) => x.id !== id));
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-content" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">My Content</h2>
        <Link to="/dashboard/upload" className="flex items-center gap-1.5 text-xs bg-[#00ff77] text-black font-bold px-3 py-1.5 rounded-full hover:bg-[#00e66b] transition-colors">
          <Upload size={12} /> Upload New
        </Link>
      </div>

      {content.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Film size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No content uploaded yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {content.map((item) => {
            const badge = STATUS_BADGE[item.status] || STATUS_BADGE.pending;
            return (
              <div key={item.id} data-testid={`content-item-${item.id}`}
                className="flex items-center gap-4 bg-[#111] border border-white/5 rounded-xl p-3 hover:bg-[#1a1a1a] transition-colors">
                <div className="w-16 h-12 rounded-lg bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                  {item.thumbnailURL ? (
                    <img src={item.thumbnailURL} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Play size={14} className="text-zinc-600" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.color}`}>{badge.label}</span>
                    <span className="text-zinc-600 text-xs flex items-center gap-1"><Eye size={10} /> {item.views || 0}</span>
                    <span className="text-zinc-600 text-xs bg-white/5 px-1.5 py-0.5 rounded-full">{item.category}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {item.status === "approved" && (
                    <Link to={`/content/${item.id}`} className="text-zinc-400 hover:text-[#00ff77] p-1.5 rounded-lg hover:bg-[#00ff77]/10 transition-colors">
                      <Eye size={14} />
                    </Link>
                  )}
                  <button onClick={() => handleDelete(item.id)} data-testid={`delete-content-${item.id}`}
                    className="text-zinc-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-400/10 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
